import React from 'react'
import { connect } from 'react-redux'
import SiteLayout from '../../../containers/layout/SiteLayout'
import HelmetTitle from '../../../components/layout/HelmetTitle'
import LoadingScreen from '../../../components/LoadingScreen'
import ContainerHeading from '../../../components/layout/ContainerHeading'
import {
  H1,
  PanelContainer,
  WhitePanel,
  ServerSuccessMessage,
  ServerErrorMessage,
  GridContainer,
} from '../../../components/my/styled-elements'

import BackToAccountNavLink from "../../../components/my/account/BackToAccountNavLink";
import UserCreateReferralForm from "../../../components/my/account/UserCreateReferralForm";
import { acceptReferralInvitation, fetchUserReferrals, referNewUser } from "../../../actions/UserActions";
import { loginIfNotAuthenticated } from "../../../utils/authUtils";
import UserAcceptReferralForm from "../../../components/my/account/UserAcceptReferralForm";

class UserReferrals extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: '',
      referrals: [],
      referralsRemaining: 3,
      referrer: null,
      serverResponse: '',
    }
  }

  componentDidMount() {
    this.loadReferrals();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props !== prevProps) {
    }
  }

  hasReferrals() {
    return Array.isArray(this.state.referrals) && this.state.referrals.length > 0;
  }

  getReferralsRemaining = () => {
    const defaultLimit = 3
    return this.state.referralsRemaining ?? defaultLimit;
  }

  loadReferrals = () => {
    const { dispatch } = this.props

    if (this.apiUser == null) {
      this.setState({
        referrals: [],
        referralsRemaining: 3,
        referrer: null,
        loading: false
      });
      return;
    }

    this.setState({ loading: true });

    dispatch(fetchUserReferrals())
      .then((response) => {
        const data = response.value.data;
        this.setState({
          referrals: data.referrals,
          referralsRemaining: data.remaining,
          referrer: data.referrer,
          loading: false
        });
      })
      .catch((err) => {
        const errorMessage = err.response ? err.response.data.errors : err.message;
        this.setState({
          error: errorMessage,
          referrer: null,
          referrals: [],
          referralsRemaining: 0,
          loading: false });
      });
  };

  handleSubmitNewReferral = ({ email }) => {
    const { dispatch } = this.props;
    dispatch(referNewUser({
      email: email.trim(),
    }))
      .then((response) => {
        const fallback = "Referral submitted!";
        this.setState({ serverResponse: response?.value?.data?.message || fallback, error: false });
      })
      .catch((error) => {
        const fallback = "There was a problem sending the referral."
        const serverError = error?.response?.data?.message;
        this.setState({ serverResponse: serverError || fallback, error: true });
      })
      .finally(() => {
        this.loadReferrals();
      })
  }

  handleSubmitAcceptReferral = ({ referralCode }) => {
    const { dispatch } = this.props;
    dispatch(acceptReferralInvitation(referralCode, {
      accept: true,
    }))
      .then((response) => {
        const fallback = "Referral accepted!";
        const responseData = response?.value?.data ?? {};
        this.setState({ serverResponse: responseData.message || fallback, error: false });
      })
      .catch((error) => {
        const fallback = "There was a problem accepting the referral."
        const serverError = error?.response?.data?.message;
        this.setState({ serverResponse: serverError || fallback, error: true });
      })
      .finally(() => {
        this.loadReferrals();
      })
  }

  renderBody = (props, state) => {
    const user = this.apiUser;
    const company = user?.company;

    return <div>
      {user && <BackToAccountNavLink/>}
      <H1>Account</H1>
      {state.serverResponse && !state.error && <ServerSuccessMessage>
        {state.serverResponse}
      </ServerSuccessMessage>}
      {state.serverResponse && state.error && <ServerErrorMessage>
        {state.serverResponse}
      </ServerErrorMessage>}
      {!user && this.renderNotLoggedInLandingPage()}
      {user && !company.paid && !state.referrer && this.renderAcceptReferralForm()}
      {user && !company.paid && state.referrer && this.renderReferralNextSteps()}
      {user && company.paid && this.renderReferUserForm()}
      {user && this.hasReferrals() && this.renderReferrals()}
    </div>
  }

  get apiUser() {
    const { apiAuth } = this.props;
    const { isSignedIn, user } = apiAuth;
    return isSignedIn ? user : null;
  }

  // This form is used by unpaid users to enter a referral code that has
  // been sent to them by another paid user.
  renderAcceptReferralForm = () => <PanelContainer>
    <ContainerHeading title={`Enter referral code`}/>
    <WhitePanel>
      <p>Got a referral code? Enter it below to get 20% cashback when you upgrade to a paid membership.</p>
      <p>Your referrer will receive 10% cashback too — it’s a win-win.</p>

      <UserAcceptReferralForm submitHandler={this.handleSubmitAcceptReferral}/>
    </WhitePanel>
  </PanelContainer>

  // This form is used by existing paid users to refer other users.
  renderReferUserForm = () => <PanelContainer>
    <ContainerHeading title={`Refer a new user`}/>
    <WhitePanel>
      <p>Loving the Mesonet and want to get your friends and neighbours involved? Refer them here and get discounts
        for you both!</p>

      <p>You can refer up to 3 different people, and you will get a 10% cashback off your Membership price for each
        successful referral. The referral will generate an email containing a unique code to your nominated person,
        which will be valid for 7 days. If your nominated person subscribes to a Mesonet membership before the 7
        days run out (i.e. a ‘successful referral’), they will receive a 20% cashback off their membership
        price.</p>

      <p>Spread the word! Limited time only!</p>

      <p><a href="/about/disclaimer">Terms and conditions</a></p>

      {(this.state.referralsRemaining <= 0) && <div>
        You have referred the maximum number of users. Please try again later.
      </div>}
      {(this.state.referralsRemaining >= 1) && <UserCreateReferralForm
        submitHandler={this.handleSubmitNewReferral}
      />}
    </WhitePanel>
  </PanelContainer>

  // this panel is shown to users that have a referrer (accepted the referral code), but
  // have not yet set up payment or paid.
  renderReferralNextSteps = () => <PanelContainer>
    <ContainerHeading title={`You’re All Set! ✅`}/>
    <WhitePanel>
      <p>Your referral code has been successfully applied.
        After you start your paid membership plan, you'll receive a 20% cashback.
        Here’s what to do next:</p>
      <ol>
        <li>
          Complete Your Account Setup:<br/>
          Go to the Account Setup Checklist to enter your account details.
        </li>
        <li>
          Choose Your Plan:<br/>
          Select a paid membership plan that suits your situation best.
        </li>
        <li>
          Enter Payment Details:<br/>
          Provide your payment information to activate your membership.
        </li>
      </ol>

      <p>If you choose to start a free trial, you'll become eligible for the referral cashback after the trial concludes.</p>
      <p>Remember, your referrer will also receive 10% cashback once your paid membership is confirmed. It’s a win-win!</p>

      <p><a href="/my/account/account_setup_checklist">Go to Account Setup Checklist</a></p>
      <p><a href="/about/disclaimer">Terms and conditions</a></p>
    </WhitePanel></PanelContainer>

  renderNotLoggedInLandingPage = () => <PanelContainer>
    <ContainerHeading title={`Accept referral`}/>
    <WhitePanel>
      <p>You must be logged in to accept the referral.</p>
      <p>Note the referral can only be accepted by the email address to which the referral was sent.</p>

      <p><a href="/login" onClick={this.loginAndRedirectBack}>Click here to log in</a></p>
    </WhitePanel></PanelContainer>

  loginAndRedirectBack = (e) => {
    e.preventDefault();

    // pre-fill login form email
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');
    if (email) localStorage.setItem('uid', email);

    loginIfNotAuthenticated(this.props);
  };

  renderReferralHeaderRow = () => <tr key={0}>
    <th style={{ textAlign: "left" }}>Status</th>
    <th style={{ textAlign: "left" }}>Email</th>
    <th style={{ textAlign: "left" }}>Referral date</th>
  </tr>

  renderReferralRows = () => this.state.referrals.map((referral, index) =>
    <tr key={index + 1}>
      <td>{referral.status_text}</td>
      <td>{referral.email}</td>
      <td>{referral.referral_date}</td>
    </tr>)

  renderReferrals = () => <GridContainer style={{ marginTop: "1rem" }}>
    <ContainerHeading title={`Referrals`}/>
    <table cellPadding={10}>
      <thead>{this.renderReferralHeaderRow()}</thead>
      <tbody>{this.renderReferralRows()}</tbody>
    </table>
  </GridContainer>

  render = () => <SiteLayout apiAuth={this.props.apiAuth}>
    <HelmetTitle title={'User Management – Refer a new user'}/>
    {this.renderBody(this.props, this.state)}
    <LoadingScreen show={this.props.apiAuth.loading}/>
  </SiteLayout>

}

export default connect(
  (store, props) => ({
    apiAuth: store.apiAuth,
  }),
)(UserReferrals)
